import { Issue } from "../models/Issue";
import { IssueRegistry } from "../models/IssueRegistry";
import { WorkAnalyzerResponse } from "../models/WorkAnalyzerResponse";
import { Conversation, glossaryTerm } from "../models/conversation";
import { InspectionRegistry } from "../models/inspectionEvaluationIssue";
import { Professional } from "../models/professional";
import { handleResponse } from './errorHandler';
import AccessTokenManager from './accessTokenManager';



//const workAnalyzerBaseUrl = 'http://localhost:3000';
const workAnalyzerBaseUrl = 'https://fxer.network';

async function upload(file : File){
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${workAnalyzerBaseUrl}/workanalyzer/upload`, {
        method: 'POST',
        body: formData,
        headers: {
            'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
          }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await handleResponse(response);
      return result;
    }catch(e){
        console.log(e);
        return undefined;
    }
}

let conversation : Conversation = { conversation: [] };


async function newConversation() : Promise<void> {
  conversation = { conversation: [] };
}

async function getInspectionEvaluation(id: string) : Promise<any>{
  try{
    const response = await fetch(`${workAnalyzerBaseUrl}/inspections/${id}`, { 
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
      }
    });
    const analyzerJsonResponse = await handleResponse(response);
    return analyzerJsonResponse.inspectionData;
  }catch(e){
    console.log(e);
    return null;
  }
}

// Status checking and polling utility functions
async function getInspectionStatus(id: string): Promise<string> {
  try {
    // Simple status endpoint - doesn't return full data
    const response = await fetch(`${workAnalyzerBaseUrl}/inspections/${id}/status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
      }
    });
    const data = await handleResponse(response);
    
    // Handle the new response format which includes more information
    if (data && typeof data === 'object' && 'status' in data) {
      console.log(`Got status for ${id}:`, data);
      return data.status;
    } else {
      console.error('Unexpected status response format:', data);
      return 'ERROR';
    }
  } catch (e) {
    console.log(e);
    return 'ERROR';
  }
}

/**
 * Polls for inspection completion with exponential backoff
 * @param id The inspection ID to poll
 * @param options Configuration options
 * @returns The final status
 */
async function pollInspectionCompletion(
  id: string, 
  options: {
    onStatusChange?: (status: string) => void;
    maxAttempts?: number;
    initialDelay?: number;
    maxDelay?: number;
    terminateOn?: string[];
  } = {}
): Promise<string> {
  const { 
    onStatusChange, 
    maxAttempts = 60, 
    initialDelay = 30000, 
    maxDelay = 30000,
    terminateOn = ['COMPLETED', 'COMPLETED_WITH_ERRORS', 'FAILED', 'SYNTHESIS_FAILED', 'ERROR']
  } = options;
  
  let attempts = 0;
  let delay = initialDelay;
  let lastStatus = '';
  
  while (attempts < maxAttempts) {
    attempts++;
    
    // Get current status
    const status = await getInspectionStatus(id);
    
    // Notify if status changed
    if (status !== lastStatus) {
      lastStatus = status;
      if (onStatusChange) onStatusChange(status);
    }
    
    // Check if we should terminate polling
    if (terminateOn.includes(status)) {
      return status;
    }
    
    // Wait with exponential backoff
    await new Promise(resolve => setTimeout(resolve, delay));
    delay = Math.min(delay * 1.5, maxDelay);
  }
  
  return lastStatus;
}

async function getInspectionDocument(id: string): Promise<any>{
    try{
      const response = await fetch(`${workAnalyzerBaseUrl}/inspections/${id}/document`, { 
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
        }
      });
      const analyzerJsonResponse = await handleResponse(response);
      return analyzerJsonResponse.documentUrl;
    }catch(e){
      console.log(e);
      return null;
    }
}

async function getInspectionRegistry() : Promise<InspectionRegistry>{
  const response = await fetch(`${workAnalyzerBaseUrl}/inspections/`, { 
    headers: {
      'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
    }
  });
  const registry = await handleResponse(response);

  return registry;
}

async function createNewInspectionEvaluation(id: string) : Promise<any>{

  try{
    const response = await fetch(`${workAnalyzerBaseUrl}/inspections/${id}/report`, { 
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
      }
    });
    const analyzerJsonResponse = await handleResponse(response);
    return analyzerJsonResponse.inspectionData;
  }catch(e){
    console.log(e);
    return null;
  }
}



async function createInspectionRecord(file : File){
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await fetch(`${workAnalyzerBaseUrl}/inspections/`, {
      method: 'POST',
      body: formData,
      headers: {
          'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
        }
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const result = await handleResponse(response);
    return result;
  }catch(e){
      console.log(e);
      return undefined;
  }
}

async function getInspectionIssueSolutions(context: string[]) : Promise<any>{

  try{

    const requestBody = {context: context};
    const response = await fetch(`${workAnalyzerBaseUrl}/inspections/solution`, { 
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
      },
      body: JSON.stringify(requestBody),
    });
    const analyzerJsonResponse = await handleResponse(response); 
    console.log(analyzerJsonResponse);
    return analyzerJsonResponse;
  }catch(e){
    console.log(e);
    return null;
  }
}

async function getImprovementItems(id: string) : Promise<any>{

  try{

    const requestBody = {imageId: id};
    const response = await fetch(`${workAnalyzerBaseUrl}/inspections/improvement`, { 
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
      },
      body: JSON.stringify(requestBody),
    });
    const analyzerJsonResponse = await handleResponse(response); 
    console.log(analyzerJsonResponse);
    return analyzerJsonResponse;
  }catch(e){
    console.log(e);
    return null;
  }
}

async function processBatchImprovements(ids: string[]) {
  try {
    // Prepare the payload with the image URLs
    const payload = {
      imageIds: ids, // The backend expects this to be named `imageIds`
    };

    // Send the payload to the backend
    const response = await fetch(`${workAnalyzerBaseUrl}/inspections/improvementBatch`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        Authorization: `Bearer ${AccessTokenManager.getAccessToken()}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error("Batch improvement submission failed");
    }

    // Handle the response
    const result = await handleResponse(response);
    return result;
  } catch (error) {
    console.error("Batch improvement error:", error);
    return null;
  }
}



async function getRepairConversation(id: string) : Promise<Issue | null>{ 
  try{
    const response = await fetch(`${workAnalyzerBaseUrl}/workanalyzer/repair/${id}`, { 
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
      }
    });
    const analyzerJsonResponse = await handleResponse(response);
    conversation.id = analyzerJsonResponse.conversation.id;
    const result = analyzerJsonResponse.conversationResponse;
    return result;
  }catch(e){
    console.log(e);
    return null;
  } 
}

async function repairInstructionsWithImage(description: string, fileId: string) : Promise<WorkAnalyzerResponse | null>{
  try {
      const lineOfConversation = { speaker: "user", text: description };
      conversation.conversation = [...conversation.conversation, lineOfConversation];
      conversation.imageId = fileId;
      const response = await fetch(`${workAnalyzerBaseUrl}/workanalyzer/repair/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
        },
        body: JSON.stringify(conversation),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const analyzerJsonResponse = await handleResponse(response);
      conversation.id = analyzerJsonResponse.conversation.id;
      const result = analyzerJsonResponse.conversationResponse;
      if(!result){
          return null;
      }
      const itemContext = analyzerJsonResponse.conversationResponse?.itemContext;
      const glossaryTerms = analyzerJsonResponse.conversationResponse?.Glossary;
      conversation.itemContext = itemContext;
      
      conversation.Glossary = glossaryTerms;


      if(result.outstandingQuestion){
          conversation.conversation.push({speaker: "Chatgpt service", text:result.outstandingQuestion.QuestionText});
      }
      return analyzerJsonResponse;
    }catch(e){
        console.log(e);
        return null;
    }
}

async function getGlossaryImage(term : glossaryTerm, conversationId: string) : Promise<string | null>{
  try{
    const url = `${workAnalyzerBaseUrl}/workanalyzer/repair/${conversationId}/imageSearch?glossaryTerm=${term.word}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
      },
    });
    const { imageUrl }  = await handleResponse(response);

    return imageUrl;
  }catch(e){
    console.log(e)
    return null;
  }
}


async function repairInstructions(description: string) : Promise<WorkAnalyzerResponse | null>{
    try {
        const lineOfConversation = { speaker: "user", text: description };
        conversation.conversation = [...conversation.conversation, lineOfConversation];
        const response = await fetch(`${workAnalyzerBaseUrl}/workanalyzer/repair`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
          },
          body: JSON.stringify(conversation),
        });
  
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
  
        const analyzerJsonResponse = await handleResponse(response);
        conversation.id = analyzerJsonResponse.conversation.id;
        const result = analyzerJsonResponse.conversationResponse;
        if(!result){
            return null;
        }

        if(result.outstandingQuestion){
            conversation.conversation.push({speaker: "Chatgpt service", text:result.outstandingQuestion.QuestionText});
        }
        return analyzerJsonResponse;
      }catch(e){
          console.log(e);
          return null;
      }
}

async function getVideoLinks(id: string): Promise<string[]>{
    const response = await fetch(`${workAnalyzerBaseUrl}/workanalyzer/repair/${id}/videos`, { 
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
      }
    });
    const embeddableLinks = await handleResponse(response);

    return embeddableLinks;
}




async function getProfessionals(category: string): Promise<Professional[]>{
    const response = await fetch(`${workAnalyzerBaseUrl}/workanalyzer/professional?category=${category}`, { 
      headers: {
        'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
      }
    });
    const professionals = await handleResponse(response);

    return professionals;
}

async function getRegistry() : Promise<IssueRegistry>{
  const response = await fetch(`${workAnalyzerBaseUrl}/workanalyzer/repair/registry`, { 
    headers: {
      'Authorization': `Bearer ${AccessTokenManager.getAccessToken()}`
    }
  });
  const registry = await handleResponse(response);

  return registry;
}

export default {
    upload,
    repairInstructions,
    getVideoLinks,
    getProfessionals,
    getRegistry,
    newConversation,
    getRepairConversation,
    repairInstructionsWithImage,
    getGlossaryImage,
    getInspectionEvaluation,
    getInspectionIssueSolutions,
    getImprovementItems,
    createInspectionRecord,
    createNewInspectionEvaluation,
    getInspectionRegistry,
    getInspectionDocument,
    processBatchImprovements,
    getInspectionStatus,
    pollInspectionCompletion
} as const;